import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FormField } from "@avamae/formbuilder";
import { useField, useFormikContext } from "formik";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";
import UploadIcon from "assets/Icon_Upload_Cloud.png";
import Dropzone, { DropEvent } from "react-dropzone";
import PDFViewer from "components/PdfViewer/PdfViewer";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";

import deleteIcon from "assets/Icon_Btn_06.png";

const tooltipMainIconSource = require("assets/Tooltip.png");
const tooltipCloseIconSource = require("assets/Icon_Btn_06.png");

const MAX_FILE_SIZE = 500000000;

const ALL_FIELDS_KEY = "ALL_FIELDS";

type AcceptedFileTypes = { [key: string]: string[] }; // MIME File Types

type FileUploadProps = {
  label: string;
  tooltip: string | null;
  isMulti?: boolean;
  acceptedFileTypes: AcceptedFileTypes;
  fieldNames: {
    singleImageOrFile: {
      imageOrFile?: any;
      imageOrFileName?: string;
      imageOrFileDownloadUri?: string;
      imageOrFileExtension?: string;
      imageOrFileFieldName?: string;
    };
    multipleImagesOrFiles?: string;
  };
  resetToClearAll: boolean | undefined;
  editReadOnly?: boolean;
};

type FileType = {
  fileName: string;
  data: File;
  preview: string;
};
const FileUpload: React.FC<FileUploadProps> = ({
  label,
  tooltip,
  isMulti,
  acceptedFileTypes,
  fieldNames,
  resetToClearAll,
  editReadOnly,
}) => {
  const { singleImageOrFile, multipleImagesOrFiles } = fieldNames;
  const {
    imageOrFile,
    imageOrFileDownloadUri,
    imageOrFileName,
    imageOrFileExtension,
    imageOrFileFieldName,
  } = singleImageOrFile;

  const [fileErrors, setFileErrors] = useState<string[]>([]);

  const [thumbnails, setThumbnails] = useState<any>(null);

  const fieldName: string =
    isMulti && multipleImagesOrFiles
      ? multipleImagesOrFiles
      : imageOrFileDownloadUri!;
  const fieldAcceptedFileTypes = fieldName
    ? acceptedFileTypes[fieldName] ?? acceptedFileTypes[ALL_FIELDS_KEY]
    : acceptedFileTypes[ALL_FIELDS_KEY];

  const {
    values,
    errors,
    setFieldValue,
    submitCount,
    setFieldError,
    setErrors,
    initialValues,
  } = useFormikContext<{ [k in string]?: any } | { [k in string]?: any }[]>();

  const relevantFormikError = useMemo(() => {
    const error: any =
      errors[
      (isMulti ? fieldName : imageOrFileFieldName) as keyof typeof errors
      ];
    return error && typeof error === "string"
      ? error
      : Array.isArray(error) &&
        error?.length > 0 &&
        error[0] &&
        typeof error[0] === "string"
        ? error[0]
        : null;
  }, [errors]);

  const onDrop = (acceptedFiles: File[]) => {
    const reader = new FileReader();

    reader.onabort = () => console.log("File reading was aborted");
    reader.onerror = () => console.log("File reading has failed");
  };

  const validateFile = (files: File[]) => {
    let errors: string[] = [];

    if (
      files.some(
        file =>
          !(
            ((!file.type || file.type.length === 0) &&
              file.name
                ?.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2)
                ?.toLowerCase() === "dwg") ||
            file.name
              ?.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2)
              ?.toLowerCase() === "msg"
          ) && !fieldAcceptedFileTypes?.includes(file.type)
      )
    ) {
      const messageCode = "PleaseSelectAValidFileTypeToUpload";
      errors = [...errors, messageCode];
    }

    if (
      MAX_FILE_SIZE &&
      files.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.size;
      }, 0) > MAX_FILE_SIZE
    ) {
      const messageCode = "TheSelectedFileExceedsTheMaximumFileSize";
      errors = [...errors, messageCode];
    }
    setFileErrors(errors);
  };

  const onDropAccepted = useCallback(
    (acceptedFiles: File[], dropEvent: DropEvent) => {
      setFileErrors([]);

      if (!isMulti && acceptedFiles && acceptedFiles?.length > 0) {
        const acceptedFile = acceptedFiles[acceptedFiles.length - 1];
        const acceptedFileName = acceptedFile.name?.split(".");
        setFieldValue(imageOrFile, acceptedFile);
        setFieldValue(fieldName, URL.createObjectURL(acceptedFile));
        imageOrFileName &&
          setFieldValue(
            imageOrFileName,
            acceptedFile.name //acceptedFileName[acceptedFileName.length - 1]
          );
        imageOrFileExtension &&
          setFieldValue(
            imageOrFileExtension,
            acceptedFileName.slice(0, acceptedFileName.length - 1) //acceptedFile.type
          );
      }
      if (isMulti && acceptedFiles?.length > 0) {
        setFieldValue(
          fieldName,
          values[fieldName as keyof typeof values]
            ? [
              ...(values[fieldName as keyof typeof values] ?? []),
              ...acceptedFiles.map(f => {
                const lastDotIndexOfFullFileName = f.name?.lastIndexOf(".");
                return {
                  [imageOrFile]: f,
                  [imageOrFileDownloadUri!]: URL.createObjectURL(f),

                    [imageOrFileName!]: f.name ?? (f as File | any).path,

                    [imageOrFileExtension!]:
                      f.name?.substring(lastDotIndexOfFullFileName + 1) ??
                      acceptedFileList
                        .find(file => file.mimeType === f.type)
                        ?.extension?.split(".")[1], // f.type
                  };
                }),
              ]
            : acceptedFiles.map(f => {
              const lastDotIndexOfFullFileName = f.name?.lastIndexOf(".");
              return {
                [imageOrFile]: f,
                [imageOrFileDownloadUri!]: URL.createObjectURL(f),

                  [imageOrFileName!]: f.name ?? (f as File | any).path,

                  [imageOrFileExtension!]:
                    f.name?.slice(lastDotIndexOfFullFileName + 1) ??
                    acceptedFileList
                      .find(file => file.mimeType === f.type)
                      ?.extension?.split(".")[1], // f.type
                };
              })
        );
      }
    },
    [values[fieldName as keyof typeof values]]
  );

  useEffect(() => {
    const imageFile = values[imageOrFile as keyof typeof values];
    const imageFileName = values[imageOrFileName as keyof typeof values];
    const imageFileExt = values[imageOrFileExtension as keyof typeof values];
    const uriOrImageFiles = values[fieldName as keyof typeof values];

    if (
      (!isMulti && (!imageFile || !imageFileName || !uriOrImageFiles)) ||
      (isMulti &&
        (!uriOrImageFiles ||
          (Array.isArray(uriOrImageFiles) && uriOrImageFiles.length === 0)))
    ) {
      setThumbnails(null);
      return;
    }
    //so now uriOrImageFiles

    //else if (file && file.fileName && file.preview) {
    const file = !isMulti
      ? imageFile
      : Array.isArray(uriOrImageFiles) && uriOrImageFiles.length > 0
        ? uriOrImageFiles[uriOrImageFiles.length - 1][imageOrFile]
        : null;
    const fileName = !isMulti
      ? imageFileName
      : Array.isArray(uriOrImageFiles) &&
        uriOrImageFiles.length > 0 &&
        imageOrFileName
        ? uriOrImageFiles[uriOrImageFiles.length - 1][imageOrFileName]
        : null;

    const fileUri =
      !isMulti && !Array.isArray(uriOrImageFiles)
        ? uriOrImageFiles
        : isMulti &&
          Array.isArray(uriOrImageFiles) &&
          uriOrImageFiles.length > 0 &&
          imageOrFileDownloadUri
          ? uriOrImageFiles[uriOrImageFiles.length - 1][imageOrFileDownloadUri]
          : null;

    const parsedFileUri = fileUri?.split(".");
    let fileFormat = "";
    if (parsedFileUri && parsedFileUri.length > 0) {
      fileFormat = parsedFileUri[parsedFileUri.length - 1];
      fileFormat = fileFormat?.split("?")[0];
    }

    const fileExt = !isMulti
      ? imageFileExt
      : Array.isArray(uriOrImageFiles) &&
        uriOrImageFiles.length > 0 &&
        imageOrFileExtension
        ? uriOrImageFiles[uriOrImageFiles.length - 1][imageOrFileExtension]
        : null;

    const parsedFileName =
      file && file.name ? file.name?.split(".") : fileName?.split(".");

    const filename = file ? file.name : fileName;
    const lastDotPosition = filename?.lastIndexOf(".");

    if (
      file &&
      ((lastDotPosition > 0 &&
        lastDotPosition !== filename?.length - 1 &&
        filename.slice(filename?.length + 1)) === "pdf" ||
        fileExt === "pdf" ||
        file.type === "application/pdf")
    ) {
      setThumbnails(
        <PDFViewer
          file={{
            data: file,
            fileName, //or file.name
            preview: fileUri ?? URL.createObjectURL(file),
          }}
          fileName={fileName}
          showTitle={false}
          overflowHidden={true}
        />
      );
    } else if (
      fileUri &&
      typeof fileUri === "string" &&
      !file &&
      //(parsedFileName && parsedFileName.length > 0 && parsedFileName[parsedFileName.length - 1] === "pdf")
      ((lastDotPosition > 0 &&
        lastDotPosition !== filename?.length - 1 &&
        filename.slice(filename?.length + 1) === "pdf") ||
        fileExt === "pdf" ||
        fileFormat === "pdf")
    ) {
      fetch(fileUri)
        .then(res => res.blob())
        .then(blob => {
          const pdfFile = new File([blob], fileName, {
            type: blob.type,
          });
          //blob.type is of pdf

          setThumbnails(
            <PDFViewer
              file={{
                data: pdfFile,
                fileName,
                preview: fileUri ?? URL.createObjectURL(pdfFile),
              }}
              fileName={fileName}
              showTitle={false}
              overflowHidden={true}
            />
          );
        });
      return;
    } else if (file && fileExt) {
      setThumbnails(
        <div className="Thumbnails" key={fileName}>
          <div className="ThumbnailsInner">
            <div className="FileExtensionText">{`.${fileExt} file`}</div>
          </div>
        </div>
      );
    } else {
      setThumbnails(
        <div className="Thumbnails" key={fileName}>
          <div className="ThumbnailsInner">
            <img
              src={fileUri}
              className="ThumbnailsImage"
              // Revoke data uri after image is loaded
              onLoad={e => {
                URL.revokeObjectURL(fileUri);
              }}
              alt="Uploaded file preview"
            />
          </div>
        </div>
      );
    }
  }, [
    values[imageOrFile as keyof typeof values],
    values[imageOrFileName as keyof typeof values],
    values[imageOrFileExtension as keyof typeof values],
    values[fieldName as keyof typeof values],
    isMulti,
  ]);

  const acceptedFileTypesToDisplay = fieldAcceptedFileTypes
    ?.map(item => {
      let parsedItem = item?.split("/")[1];
      if (parsedItem === "msword") {
        parsedItem = "word";
      }
      return parsedItem;
    })
    .join(", ");

  const resetFile = () => {
    if (resetToClearAll) {
      setFieldValue(fieldName, null);
      setFieldValue(imageOrFile, null);
      imageOrFileName && setFieldValue(imageOrFileName, null);
      imageOrFileExtension && setFieldValue(imageOrFileExtension, null);
      return;
    }
    setFieldValue(
      fieldName,
      initialValues[fieldName as keyof typeof initialValues]
    );
    if (!isMulti) {
      setFieldValue(
        imageOrFile,
        initialValues[imageOrFile as keyof typeof initialValues] //or null
      );
      imageOrFileName &&
        setFieldValue(
          imageOrFileName,
          initialValues[imageOrFileName as keyof typeof initialValues]
        );
      imageOrFileExtension &&
        setFieldValue(
          imageOrFileExtension,
          initialValues[imageOrFileExtension as keyof typeof initialValues]
        );
    }
  };

  const handleDeleteFile = (idx: number) => {
    if (
      fieldName &&
      values &&
      typeof values === "object" &&
      values.hasOwnProperty(fieldName)
    ) {
      const fieldValues = values[fieldName as keyof typeof values];

      if (
        fieldValues &&
        Array.isArray(fieldValues) &&
        fieldValues.length > idx
      ) {
        let newValues = [...fieldValues];

        newValues = newValues.filter((item, idx2) => idx2 !== idx);
        // newValues = newValues.splice(idx, 1); // Not working

        setFieldValue(fieldName, newValues);

        setThumbnails(null);
      } else if (!isMulti) {
        setFieldValue(fieldName, null);
        setFieldValue(imageOrFile, null);
        imageOrFileName && setFieldValue(imageOrFileName, null);
        imageOrFileExtension && setFieldValue(imageOrFileExtension, null);
      }
    }
  };

  useEffect(() => {
    if (fileErrors?.length > 0)
      toast(
        <ToastMessage
          toastData={[
            {
              title: "File Upload Failed",
              text: convertedString(fileErrors[0]),
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
  }, [fileErrors]);

  return (
    <div
      className={`FormBox FormSideBySide FileUploadFieldRendererFormBox ${
        editReadOnly ? "EditReadOnly" : ""
      }`}
    >
      <div className={`FormLabel`}>
        <label>{label}</label>

        {tooltip && (
          <FormFieldTooltip
            tooltipText={tooltip}
            tooltipMainIconSource={tooltipMainIconSource}
            tooltipCloseIconSource={tooltipCloseIconSource}
          />
        )}
      </div>
      <div className={`FormField`}>
        <div
          className={`FileUploadFieldRenderer ${relevantFormikError && submitCount > 0 ? "Error" : ""
            }`}
        >
          <div className="DataProcessing">
            (Note that it may take a few minutes for documents to be
            uploaded...)
          </div>

          <Dropzone
            onDrop={(acceptedFiles, rejectedFiles, event) => {
              if (!editReadOnly) {
                onDrop(acceptedFiles);
              }
            }}
            onDropRejected={validateFile}
            onDropAccepted={onDropAccepted}
            accept={fieldAcceptedFileTypes}
            maxSize={MAX_FILE_SIZE}
            multiple={isMulti}
            disabled={editReadOnly}
          >
            {({ getRootProps, getInputProps }) => (
              <div className={`DropzoneContainer`} {...getRootProps()}>
                <div className="Contents">
                  <img className={`UploadIcon`} src={UploadIcon} alt="Upload" />
                  <input {...getInputProps()} />

                  <h3>File</h3>
                  <span>Click or drag to upload</span>
                </div>
                {thumbnails && (
                  <div className="ThumbnailsContainer">{thumbnails}</div>
                )}
              </div>
            )}
          </Dropzone>

          <div className="SubContainers">
            <div className="SubContainer Container1">
              <div className="SubContainerItem">
                <div className="Title">{"Accepts - "}</div>

                <div className="Text">
                  {fieldAcceptedFileTypes
                    ?.map(
                      t => acceptedFileList.find(f => f.mimeType === t)?.format
                    )
                    ?.filter(
                      (value, index, self) => self.indexOf(value) === index
                    )
                    ?.join(", ")}
                </div>
              </div>

              <div className="SubContainerItem">
                <div className="Title">{"Size limit - "}</div>
                <div className="Text">500MB</div>
              </div>
            </div>

            <div className="SubContainer Container2">
              <div
                className={`Btn ResetBtn ${
                  (!isMulti &&
                    !values[imageOrFileDownloadUri as keyof typeof values]) ||
                    (isMulti &&
                      Array.isArray(values[fieldName as keyof typeof values]) &&
                      values[fieldName as keyof typeof values]?.every(
                        (f: any) => !f[imageOrFileDownloadUri as keyof typeof f]
                      ))
                    ? "Disabled"
                    : ""
                  }`}
                onClick={resetFile}
              >
                Reset
              </div>
            </div>
          </div>

          {!isMulti &&
            values[imageOrFileName as keyof typeof values] &&
            values[fieldName as keyof typeof values] && (
              <div className="UploadedFile">
                <FileDetails
                  name={values[imageOrFileName as keyof typeof values]}
                  extension={
                    values[imageOrFileExtension as keyof typeof values]
                  }
                  extensionChecker={[
                    getStringPart(values[fieldName as keyof typeof values]),
                    ...(acceptedFileList
                      ?.filter(
                        file =>
                          file.mimeType ===
                          values[imageOrFile as keyof typeof values].type
                      )
                      ?.map(file => file.extension.split(".")[1]) ?? []),
                  ]}
                  onDelete={() => {
                    handleDeleteFile(0);
                  }}
                />
              </div>
            )}
          {isMulti && Array.isArray(values[fieldName as keyof typeof values]) && (
            <div className="UploadedFile">
              {values[fieldName as keyof typeof values]?.map(
                (f: any, idx: number) => {
                  return (
                    <FileDetails
                      name={f[imageOrFileName as keyof typeof f]}
                      extension={f[imageOrFileExtension as keyof typeof f]}
                      extensionChecker={[
                        getStringPart(f[imageOrFileDownloadUri as any]),
                        ...(acceptedFileList
                          ?.filter(
                            file =>
                              file.mimeType ===
                              (f[imageOrFile as keyof typeof f] as any)?.type
                          )
                          ?.map(file => file.extension.split(".")[1]) ?? []),
                      ]}
                      onDelete={() => {
                        handleDeleteFile(idx);
                      }}
                    />
                  );

                  //show ext: if fileExt exists then replaces imageOrFileName's ext (if it has ext) or adds to imageOrFileName
                  //imageOrFileName or deconstd imageOrFileName (prefix of imageOrFileName's ext) + fileExt or imageOrFileName's ext
                }
              )}
            </div>
          )}
        </div>

        {relevantFormikError && submitCount > 0 && (
          //(!fileErrors || fileErrors?.length === 0) &&
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    </div>
  );
};

const convertedString = (inputString: string) =>
  inputString
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());

const FileDetails = ({
  name,
  extension,
  extensionChecker,
  onDelete,
}: {
  name: string;
  extension: string;
  extensionChecker?: string[];
  onDelete?: () => void;
}) => {
  let nameWithoutExt = name;
  if (hasFileExtension(name)) {
    const nameExt = getFileExtension(name);
    if (
      nameExt &&
      (nameExt === extension ||
        (nameExt && extensionChecker?.includes(nameExt)) ||
        acceptedFileList.some(f => f.extension === "." + nameExt))
    ) {
      nameWithoutExt = getFileNameWithoutExtension(name);
    }
  }

  return (
    <>
      {/* <div className="Title">Uploaded file: </div> */}
      <div className="Text">
        <span>{nameWithoutExt}</span>
        <span>
          .
          {extension ??
            getFileExtension(name) ??
            extensionChecker?.find(
              e =>
                e &&
                e.length > 0 &&
                acceptedFileList.some(f => f.extension === "." + e)
            )}
        </span>

        {onDelete && (
          <div className="DeleteBtn" onClick={onDelete}>
            <img src={deleteIcon} alt="delete file" className="DeleteIcon" />
          </div>
        )}
      </div>
    </>
  );
};

function hasFileExtension(filename: string): boolean {
  // Regex to check if filename has an extension: e.g., .txt, .jpg, etc.
  const fileExtensionPattern = /\.[0-9a-z]+$/i;
  return fileExtensionPattern.test(filename);
}
function getFileExtension(filename: string): string | null {
  const parts = filename.split(".");
  // If there's no dot or the filename ends with a dot, return null
  if (parts.length < 2 || filename.endsWith(".")) {
    return null;
  }
  return parts.pop() || null;
}
function getFileNameWithoutExtension(fullName: string): string {
  // Split the full name at each dot
  const parts = fullName.split(".");
  // If there's no dot or only one part, return the original fullName
  if (parts.length < 2) {
    return fullName;
  }
  // Remove the last part (which is the extension) and join the remaining parts with a dot
  parts.pop();
  return parts.join(".");
}
function getStringPart(str: string): string {
  // Split the string at each dot
  const parts = str?.split(".");
  // If there's no dot, return an empty string
  if (parts?.length < 2) {
    return "";
  }
  // Take the last part after the dot
  let lastPart = parts?.pop() || "";
  // Check if there's a '?'
  const questionMarkIndex = lastPart?.indexOf("?");
  if (questionMarkIndex !== -1) {
    // Take only the part of the string before the '?'
    lastPart = lastPart?.substring(0, questionMarkIndex);
  }
  return lastPart;
}

type FileUploadReadOnlyProps = {
  name: string;
  label: string;
  tooltip: string | null;
  isMulti?: boolean;
};

const FileUploadReadOnly: React.FC<FileUploadReadOnlyProps> = ({
  name,
  label,
  tooltip,
  isMulti,
}) => {
  const [field] = useField<any>(name);

  const fileUri =
    (isMulti || Array.isArray(field.value)) && field.value?.length > 0
      ? field.value[field.value.length - 1]
      : field.value;
  const parsedFileUri = fileUri?.split(".");
  const fileFormat =
    parsedFileUri?.length > 0
      ? parsedFileUri[parsedFileUri.length - 1]?.split("?")[0]
      : "";

  return (
    <div className={`FormBox FormSideBySide `}>
      <div className="FormLabel">
        <label>File</label>
        {/* <label>{label}</label> */}

        {tooltip && (
          <FormFieldTooltip
            tooltipText={tooltip}
            tooltipMainIconSource={tooltipMainIconSource}
            tooltipCloseIconSource={tooltipCloseIconSource}
          />
        )}
      </div>

      <div className="FormField">
        <div className={`FileUploadFieldRenderer ReadOnly`}>
          {(field.value ||
            (Array.isArray(field.value) && field.value?.length > 0)) &&
            (fileUri?.length > 0 || fileFormat) ? ( //fileFormat && fileFormat?.length>0 //fileUri or fileFormat as string
            fileFormat === "pdf" ? (
              <PDFViewer
                file={fileUri}
                fileName={field.name ?? name}
                showTitle={false}
              />
            ) : (
              <div className="ThumbnailsContainer">
                <div className="Thumbnails" key={field.name ?? name}>
                  <div className="ThumbnailsInner">
                    <img
                      src={fileUri}
                      className="ThumbnailsImage"
                      // Revoke data uri after image is loaded
                      onLoad={() => {
                        URL.revokeObjectURL(fileUri);
                      }}
                      alt="Uploaded file preview"
                    />
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="NoData">
              <div className="Text">No file uploaded</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const multiFileUploadFieldRenderer = ({
  readOnly,
  fieldNames,
  isMulti,
  acceptedFileTypes,
  resetToClearAll,
  editReadOnly,
}: {
  readOnly: boolean;
  fieldNames: {
    singleImageOrFile: {
      imageOrFile?: any;
      imageOrFileName?: string;
      imageOrFileDownloadUri?: string;
      imageOrFileExtension?: string;
      imageOrFileFieldName?: string;
    };
    multipleImagesOrFiles?: string;
  };
  isMulti?: boolean;
  acceptedFileTypes?: AcceptedFileTypes; // MIME File Types
  resetToClearAll?: boolean;
  editReadOnly?: boolean;
}) =>
  function fileUploadFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    let tempAcceptedFileTypes: AcceptedFileTypes = {
      ...acceptedFileTypes,
    };

    tempAcceptedFileTypes[ALL_FIELDS_KEY] = [
      ...new Set(acceptedFileList.map(f => f.mimeType)),
    ];

    if (readOnly) {
      return (
        <FileUploadReadOnly
          name={String(field.name)}
          label={field.label}
          tooltip={field.toolTip ? field.toolTip : null}
        />
      );
    } else {
      return (
        <FileUpload
          //name={String(field.name)}
          fieldNames={fieldNames}
          label={field.label}
          tooltip={field.toolTip ? field.toolTip : null}
          acceptedFileTypes={tempAcceptedFileTypes}
          isMulti={isMulti}
          resetToClearAll={resetToClearAll}
          editReadOnly={editReadOnly}
        />
      );
    }
  };

export const acceptedFileList = [
  { format: "PDF", extension: ".pdf", mimeType: "application/pdf" },
  { format: "Word", extension: ".doc", mimeType: "application/msword" },
  {
    format: "Word",
    extension: ".docx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    format: "PowerPoint",
    extension: ".ppt",
    mimeType: "application/vnd.ms-powerpoint",
  },
  {
    format: "PowerPoint",
    extension: ".pptx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  { format: "Excel", extension: ".xls", mimeType: "application/vnd.ms-excel" },
  {
    format: "Excel",
    extension: ".xlsx",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  { format: "CSV", extension: ".csv", mimeType: "text/csv" },
  { format: "MP4", extension: ".mp4", mimeType: "video/mp4" },
  { format: "JPEG", extension: ".jpg", mimeType: "image/jpeg" }, // note that .jpeg and .jpg are common extensions for the same format
  { format: "JPEG", extension: ".jpeg", mimeType: "image/jpeg" },
  { format: "PNG", extension: ".png", mimeType: "image/png" },
  { format: "GIF", extension: ".gif", mimeType: "image/gif" },
  {
    format: "DWG",
    extension: ".dwg,.dxf,*.acd",
    mimeType: "image/vnd.dwg",
  },
  {
    format: "DWG",
    extension: ".dwg,.dxf,*.acd",
    mimeType: "image/vnd.dxf",
  },
  {
    format: "DWG",
    extension: ".dwg,.dxf,*.acd",
    mimeType: "application/dwg",
  },
  {
    format: "DWG",
    extension: ".dwg,.dxf,*.acd",
    mimeType: ".dwg",
  },
  {
    format: "DXF",
    extension: ".dxf",
    mimeType: ".dxf",
  },
  { format: "MSG", extension: ".msg", mimeType: "application/vnd.ms-outlook" },
  { format: "MSG", extension: ".msg", mimeType: ".msg" },
  { format: "AI", extension: ".ai", mimeType: ".ai" },
  { format: "XLSM", extension: ".xlsm", mimeType: "application/vnd.ms-excel.sheet.macroEnabled.12" },
  { format: "XLS", extension: ".xls", mimeType: "application/vnd.ms-excel" }
];
